import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { api, formatDate } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it, de, fr, enGB } from 'date-fns/locale';
import { 
  ClockCounterClockwise,
  SignIn,
  CloudArrowUp,
  DownloadSimple,
  Trash,
  PencilSimple,
  Eye,
  Key,
  Export,
  CalendarBlank,
  FileZip,
  FileCsv,
  Archive
} from '@phosphor-icons/react';

const actionIcons = {
  login: SignIn,
  upload: CloudArrowUp,
  download: DownloadSimple,
  delete: Trash,
  create: PencilSimple,
  update: PencilSimple,
  view: Eye,
  password_change: Key,
  export: Export
};

const localeMap = { it: 'it-CH', de: 'de-CH', fr: 'fr-CH', en: 'en-GB' };
const dateLocales = { it, de, fr, en: enGB };

export const AuditLogPage = () => {
  const { t, language } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Export date range
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [exportFilter, setExportFilter] = useState('all');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const resourceType = filter === 'all' ? null : filter;
        const res = await api.getAuditLogs(200, resourceType);
        setLogs(res.data);
      } catch (error) {
        console.error('Failed to fetch audit logs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [filter]);

  const getActionClass = (action) => {
    const classes = {
      login: 'audit-action login',
      upload: 'audit-action upload',
      download: 'audit-action download',
      delete: 'audit-action delete',
      create: 'audit-action create',
      update: 'audit-action update',
      view: 'audit-action view',
      password_change: 'audit-action update',
      export: 'audit-action download'
    };
    return classes[action] || 'audit-action';
  };

  const handleExport = async (exportType = 'csv') => {
    if (!dateFrom || !dateTo) {
      toast.error('Seleziona entrambe le date');
      return;
    }
    
    if (dateFrom > dateTo) {
      toast.error('La data di inizio deve essere precedente alla data di fine');
      return;
    }
    
    setExporting(true);
    try {
      const fromStr = format(dateFrom, 'yyyy-MM-dd');
      const toStr = format(dateTo, 'yyyy-MM-dd');
      
      if (exportType === 'zip') {
        await api.exportAuditArchive(fromStr, toStr, exportFilter);
        toast.success('Archivio ZIP scaricato con successo');
      } else {
        await api.exportAuditLogs(fromStr, toStr, exportFilter);
        toast.success(t('common.success'));
      }
      setShowExportDialog(false);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setExporting(false);
    }
  };

  const openExportDialog = () => {
    // Default: last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setDateFrom(thirtyDaysAgo);
    setDateTo(today);
    setExportFilter('all');
    setShowExportDialog(true);
  };

  const formatSelectedDate = (date) => {
    if (!date) return '';
    return format(date, 'dd MMM yyyy', { locale: dateLocales[language] || dateLocales.it });
  };

  return (
    <Layout>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <h1 className="page-title" data-testid="audit-title">
            {t('audit.title')}
          </h1>
          <div className="flex items-center gap-4">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px] rounded-sm" data-testid="audit-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('audit.all')}</SelectItem>
                <SelectItem value="document">{t('audit.resources.document')}</SelectItem>
                <SelectItem value="folder">{t('audit.resources.folder')}</SelectItem>
                <SelectItem value="user">{t('audit.resources.user')}</SelectItem>
                <SelectItem value="email">{t('audit.resources.email')}</SelectItem>
                <SelectItem value="workflow">{t('audit.resources.workflow')}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={openExportDialog}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
              data-testid="export-btn"
            >
              <Export size={18} className="mr-2" />
              {t('audit.export')}
            </Button>
          </div>
        </div>
      </div>

      <div className="content-area">
        <div className="bg-white border border-[#E4E4E7]">
          {loading ? (
            <div className="p-8 text-center text-[#52525B]">
              {t('common.loading')}
            </div>
          ) : logs.length === 0 ? (
            <div className="empty-state py-16">
              <ClockCounterClockwise className="empty-state-icon" size={48} />
              <p className="empty-state-title">{t('common.noData')}</p>
            </div>
          ) : (
            <table className="w-full table-swiss">
              <thead>
                <tr>
                  <th className="text-left px-6 py-4">{t('audit.timestamp')}</th>
                  <th className="text-left px-6 py-4">{t('audit.action')}</th>
                  <th className="text-left px-6 py-4">{t('audit.resource')}</th>
                  <th className="text-left px-6 py-4">{t('audit.user')}</th>
                  <th className="text-left px-6 py-4">{t('audit.details')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const ActionIcon = actionIcons[log.action] || Eye;
                  return (
                    <tr key={log.id} data-testid={`audit-row-${log.id}`}>
                      <td className="px-6 py-4 text-sm text-[#52525B] font-mono">
                        {formatDate(log.timestamp, localeMap[language])}
                      </td>
                      <td className="px-6 py-4">
                        <span className={getActionClass(log.action)}>
                          <ActionIcon size={14} className="mr-1" />
                          {(() => {
                            const translated = t(`audit.actions.${log.action}`);
                            // If translation key is returned as-is, format the action nicely
                            if (translated === `audit.actions.${log.action}` || translated.startsWith('audit.actions.')) {
                              return log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            }
                            return translated;
                          })()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <span className="text-xs font-medium uppercase tracking-wider text-[#52525B]">
                            {(() => {
                              const translated = t(`audit.resources.${log.resource_type}`);
                              if (translated === `audit.resources.${log.resource_type}` || translated.startsWith('audit.resources.')) {
                                return log.resource_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                              }
                              return translated;
                            })()}
                          </span>
                          {log.resource_name && (
                            <p className="text-sm text-[#09090B] truncate max-w-[200px]">
                              {log.resource_name}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#09090B]">
                        {log.user_email}
                      </td>
                      <td className="px-6 py-4">
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="text-xs text-[#52525B]">
                            {log.details.size && (
                              <span className="mr-3">Size: {log.details.size}</span>
                            )}
                            {log.details.hash && (
                              <span className="hash-display text-[10px]">
                                {log.details.hash.substring(0, 12)}...
                              </span>
                            )}
                            {log.details.count !== undefined && (
                              <span>Records: {log.details.count}</span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('audit.exportTitle')}</DialogTitle>
            <DialogDescription className="text-sm text-[#52525B]">
              Seleziona il periodo e il formato di esportazione. L'archivio ZIP include i documenti originali.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Date From */}
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('audit.dateFrom')}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal mt-1 rounded-sm"
                    data-testid="date-from-btn"
                  >
                    <CalendarBlank size={18} className="mr-2 text-[#52525B]" />
                    {dateFrom ? formatSelectedDate(dateFrom) : 'Seleziona data'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={setDateFrom}
                    locale={dateLocales[language] || dateLocales.it}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date To */}
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('audit.dateTo')}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal mt-1 rounded-sm"
                    data-testid="date-to-btn"
                  >
                    <CalendarBlank size={18} className="mr-2 text-[#52525B]" />
                    {dateTo ? formatSelectedDate(dateTo) : 'Seleziona data'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={setDateTo}
                    locale={dateLocales[language] || dateLocales.it}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Resource Type Filter */}
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('audit.resource')}
              </Label>
              <Select value={exportFilter} onValueChange={setExportFilter}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="export-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('audit.all')}</SelectItem>
                  <SelectItem value="document">{t('audit.resources.document')}</SelectItem>
                  <SelectItem value="folder">{t('audit.resources.folder')}</SelectItem>
                  <SelectItem value="user">{t('audit.resources.user')}</SelectItem>
                  <SelectItem value="email">{t('audit.resources.email')}</SelectItem>
                  <SelectItem value="workflow">{t('audit.resources.workflow')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowExportDialog(false)}
              className="rounded-sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => handleExport('csv')}
              disabled={exporting || !dateFrom || !dateTo}
              variant="outline"
              className="rounded-sm border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/10"
              data-testid="export-csv-btn"
            >
              <FileCsv size={18} className="mr-2" />
              {exporting ? t('common.loading') : 'Esporta CSV'}
            </Button>
            <Button
              onClick={() => handleExport('zip')}
              disabled={exporting || !dateFrom || !dateTo}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
              data-testid="export-zip-btn"
            >
              <Archive size={18} className="mr-2" />
              {exporting ? t('common.loading') : 'Esporta ZIP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
